import {
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'
import { Mutex } from 'async-mutex'
import { BaseQueryApi } from '@reduxjs/toolkit/query'

export interface UpdateStoreRequest {
  font?: string
  textColor?: string
  backgroundColor?: string
  text?: string
  backgroundImageUrl?: string
}

export interface Store {
  id: string
  name: string
  type: string
  location: string
  logoUrl: string
  backgroundColor?: string
  textColor: string
  font: string
  text: string
  backgroundImageUrl?: string
  numberOfStamps: number
  stampImageUrl: string
  totpSecret: string
}

const mutex = new Mutex()

const baseQuery = fetchBaseQuery({
  baseUrl: '',
  prepareHeaders: (headers) => {
    const userId = localStorage.getItem('userId')
    if (userId) {
      headers.set('X-User-Id', userId)
    }

    const token = localStorage.getItem('adminToken')
    if (token) {
      headers.set('Authorization', token)
    }

    return headers
  },
})

const baseQueryWithAuth = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: object,
) => {
  await mutex.waitForUnlock()

  let result = await baseQuery(args, api, extraOptions)
  if (result.error && (result.error as FetchBaseQueryError).status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire()
      try {
        window.location.href = '/login'
      } finally {
        release()
      }
    } else {
      await mutex.waitForUnlock()
      result = await baseQuery(args, api, extraOptions)
    }
  }
  return result
}

export const brandingApi = createApi({
  reducerPath: 'brandingApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Branding'],
  endpoints: (builder) => ({
    uploadStoreLogo: builder.mutation<void, FormData>({
      query: (payload: FormData) => ({
        url: `https://upload-store-logo.gyore-soma.workers.dev`,
        method: 'POST',
        body: payload,
        formData: true,
      }),
      invalidatesTags: ['Branding'],
    }),
    uploadBackgroundImage: builder.mutation<void, FormData>({
      query: (payload: FormData) => ({
        url: `https://upload-background-image.gyore-soma.workers.dev`,
        method: 'POST',
        body: payload,
        formData: true,
      }),
      invalidatesTags: ['Branding'],
    }),
    uploadStampImage: builder.mutation<void, FormData>({
      query: (payload: FormData) => ({
        url: `https://upload-stamp-image.gyore-soma.workers.dev`,
        method: 'POST',
        body: payload,
        formData: true,
      }),
      invalidatesTags: ['Branding'],
    }),
    updateStore: builder.mutation<void, UpdateStoreRequest>({
      query: (payload: UpdateStoreRequest) => ({
        url: `https://update-store.gyore-soma.workers.dev`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: ['Branding'],
    }),
    getStoreData: builder.query<Store, void>({
      query: () => ({
        url: `https://get-admin-store.gyore-soma.workers.dev`,
      }),
      providesTags: ['Branding'],
    }),
  }),
})

export const {
  useUploadStoreLogoMutation,
  useUploadBackgroundImageMutation,
  useUpdateStoreMutation,
  useUploadStampImageMutation,
  useGetStoreDataQuery,
} = brandingApi
